export interface SubstratePreset {
  id: string;
  name: string;
  category: 'Stickers' | 'Paper' | 'Film' | 'Cardstock' | 'Synthetic' | 'Specialty' | 'Custom';
  defaultCost: number; // in base currency (default INR ~12, USD ~0.15)
  weightGsm: number;
  description: string;
  isCustom?: boolean;
}

export const DEFAULT_SUBSTRATE_PRESETS: SubstratePreset[] = [
  {
    id: 'standard_vinyl',
    name: 'Self-Adhesive Vinyl (White)',
    category: 'Stickers',
    defaultCost: 12,
    weightGsm: 140,
    description: 'General purpose matte/gloss indoor & outdoor adhesive sheets.'
  },
  {
    id: 'gloss_bopp',
    name: 'Waterproof Gloss BOPP',
    category: 'Film',
    defaultCost: 18,
    weightGsm: 160,
    description: 'High-gloss water, oil, and chemical resistant label stock.'
  },
  {
    id: 'matte_synthetic',
    name: 'Matte Synthetic Film (Yupo/PP)',
    category: 'Synthetic',
    defaultCost: 15,
    weightGsm: 150,
    description: 'Tear-proof waterproof synthetic sheet for cosmetic & industrial stickers.'
  },
  {
    id: 'kraft_paper',
    name: 'Eco Matte Kraft Paper',
    category: 'Paper',
    defaultCost: 9,
    weightGsm: 120,
    description: 'Rustic, biodegradable unbleached organic label material.'
  },
  {
    id: 'cardstock_300',
    name: 'Heavy Cardstock 300gsm',
    category: 'Cardstock',
    defaultCost: 6,
    weightGsm: 300,
    description: 'Rigid apparel tags, coupon inserts, and badge cards.'
  },
  {
    id: 'cardstock_350',
    name: 'Ultra-Heavy Board 350gsm',
    category: 'Cardstock',
    defaultCost: 8,
    weightGsm: 350,
    description: 'Ultra-thick packaging tags, hangtags, and premium cards.'
  },
  {
    id: 'clear_vinyl',
    name: 'Ultra-Clear Transparent Film',
    category: 'Film',
    defaultCost: 22,
    weightGsm: 130,
    description: 'Optically clear see-through sticker for glass jars and clear bottles.'
  },
  {
    id: 'metallic_silver',
    name: 'Brushed Silver / Chrome Foil',
    category: 'Specialty',
    defaultCost: 28,
    weightGsm: 175,
    description: 'Reflective mirror silver metallic substrate for luxury electronics & appliances.'
  },
  {
    id: 'gold_foil',
    name: '24K Bright Gold Metallic Sheet',
    category: 'Specialty',
    defaultCost: 32,
    weightGsm: 180,
    description: 'Rich lustrous gold sheen for luxury certificates, wine, and VIP packaging.'
  },
  {
    id: 'holographic_rainbow',
    name: 'Laser Holographic Rainbow Film',
    category: 'Specialty',
    defaultCost: 35,
    weightGsm: 165,
    description: 'Dynamic iridescent prism rainbow effect for collector & security stickers.'
  },
  {
    id: 'custom',
    name: '+ Add Custom Material / Substrate...',
    category: 'Custom',
    defaultCost: 15,
    weightGsm: 150,
    description: 'Specify custom material cost, weight, and properties.'
  }
];

const STORAGE_KEY = 'saas_custom_substrates_v1';

export function getAvailableSubstrates(): SubstratePreset[] {
  if (typeof window === 'undefined') return DEFAULT_SUBSTRATE_PRESETS;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_SUBSTRATE_PRESETS;
    const userCustom: SubstratePreset[] = JSON.parse(raw);
    const baseList = DEFAULT_SUBSTRATE_PRESETS.filter(p => p.id !== 'custom');
    const customOption = DEFAULT_SUBSTRATE_PRESETS.find(p => p.id === 'custom');
    return [...baseList, ...userCustom, customOption as SubstratePreset];
  } catch (err) {
    console.error('Failed to load custom substrates from storage', err);
    return DEFAULT_SUBSTRATE_PRESETS;
  }
}

export async function getAvailableSubstratesAsync(): Promise<SubstratePreset[]> {
  return getAvailableSubstrates();
}

export async function saveCustomSubstrate(preset: Omit<SubstratePreset, 'id' | 'isCustom'>): Promise<SubstratePreset> {
  const newPreset: SubstratePreset = {
    ...preset,
    id: `custom_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
    isCustom: true
  };

  if (typeof window !== 'undefined') {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const list: SubstratePreset[] = raw ? JSON.parse(raw) : [];
      const filtered = list.filter((p) => p.id !== newPreset.id);
      filtered.push(newPreset);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
    } catch (err) {
      console.warn('LocalStorage save warning:', err);
    }
  }
  return newPreset;
}

export function updateCustomSubstrate(id: string, updates: Partial<SubstratePreset>): void {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const list: SubstratePreset[] = raw ? JSON.parse(raw) : [];
    const index = list.findIndex(p => p.id === id);
    if (index >= 0) {
      list[index] = { ...list[index], ...updates };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    }
  } catch (err) {
    console.error('Failed to update custom substrate', err);
  }
}

export async function deleteCustomSubstrate(id: string): Promise<void> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const list = JSON.parse(raw);
    const filtered = list.filter((p: any) => p.id !== id);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
  } catch (err) {}
}

export function findSubstratePreset(id?: string, substratesList?: SubstratePreset[]): SubstratePreset {
  const list = substratesList || getAvailableSubstrates();
  return list.find(p => p.id === id) || DEFAULT_SUBSTRATE_PRESETS[0];
}
